import React, { useState } from 'react';
import MainHeading from '../Common/MainHeading';
import { useLocale } from '../../localization/LocaleContext';
import './WorkflowSteps.css';

const STEP_ICONS = {
    capture: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
        </svg>
    ),
    estimate: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <path d="M8 1 C8.5 4.5 11.5 7.5 16 9 C11.5 10.5 8.5 13.5 8 17 C7.5 13.5 4.5 10.5 0 9 C4.5 7.5 7.5 4.5 8 1 Z"/>
            <path d="M18 13 C18.25 14.5 19.75 16.5 22 17 C19.75 17.5 18.25 19.5 18 21 C17.75 19.5 16.25 17.5 14 17 C16.25 16.5 17.75 14.5 18 13 Z"/>
        </svg>
    ),
    quote: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="9" y1="13" x2="15" y2="13"/>
            <line x1="9" y1="17" x2="15" y2="17"/>
        </svg>
    ),
    invoice: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2"/>
            <line x1="7" y1="9" x2="17" y2="9"/>
            <line x1="7" y1="13" x2="13" y2="13"/>
            <path d="M7 17h4"/>
        </svg>
    ),
    payment: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M16 8H9.5a2.5 2.5 0 0 0 0 5h3a2.5 2.5 0 0 1 0 5H8"/>
            <line x1="12" y1="6" x2="12" y2="8"/>
            <line x1="12" y1="16" x2="12" y2="18"/>
        </svg>
    ),
};

export default function WorkflowSteps() {
    const { t } = useLocale();
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            key: 'capture',
            icon: STEP_ICONS.capture,
            pill: t('homeWorkflow.chainCapture'),
            cardTitle: t('homeWorkflow.step1Title'),
            cardDescription: t('homeWorkflow.step1Description'),
            previewBadge: t('homeWorkflow.preview1Badge'),
            previewTitle: t('homeWorkflow.preview1Title'),
            previewDescription: t('homeWorkflow.preview1Description'),
            checklist: [
                t('homeWorkflow.preview1Check1'),
                t('homeWorkflow.preview1Check2'),
                t('homeWorkflow.preview1Check3'),
            ],
            screenTag: t('homeWorkflow.preview1ScreenTag'),
        },
        {
            key: 'estimate',
            icon: STEP_ICONS.estimate,
            pill: t('homeWorkflow.chainEstimate'),
            cardTitle: t('homeWorkflow.step2Title'),
            cardDescription: t('homeWorkflow.step2Description'),
            previewBadge: t('homeWorkflow.preview2Badge'),
            previewTitle: t('homeWorkflow.preview2Title'),
            previewDescription: t('homeWorkflow.preview2Description'),
            checklist: [
                t('homeWorkflow.preview2Check1'),
                t('homeWorkflow.preview2Check2'),
                t('homeWorkflow.preview2Check3'),
            ],
            screenTag: t('homeWorkflow.preview2ScreenTag'),
        },
        {
            key: 'quote',
            icon: STEP_ICONS.quote,
            pill: t('homeWorkflow.chainQuote'),
            cardTitle: t('homeWorkflow.step3Title'),
            cardDescription: t('homeWorkflow.step3Description'),
            previewBadge: t('homeWorkflow.preview3Badge'),
            previewTitle: t('homeWorkflow.preview3Title'),
            previewDescription: t('homeWorkflow.preview3Description'),
            checklist: [
                t('homeWorkflow.preview3Check1'),
                t('homeWorkflow.preview3Check2'),
                t('homeWorkflow.preview3Check3'),
            ],
            screenTag: t('homeWorkflow.preview3ScreenTag'),
        },
        {
            key: 'invoice',
            icon: STEP_ICONS.invoice,
            pill: t('homeWorkflow.chainInvoice'),
            cardTitle: t('homeWorkflow.step4Title'),
            cardDescription: t('homeWorkflow.step4Description'),
            previewBadge: t('homeWorkflow.preview4Badge'),
            previewTitle: t('homeWorkflow.preview4Title'),
            previewDescription: t('homeWorkflow.preview4Description'),
            checklist: [
                t('homeWorkflow.preview4Check1'),
                t('homeWorkflow.preview4Check2'),
                t('homeWorkflow.preview4Check3'),
            ],
            screenTag: t('homeWorkflow.preview4ScreenTag'),
        },
        {
            key: 'payment',
            icon: STEP_ICONS.payment,
            pill: t('homeWorkflow.chainPayment'),
            cardTitle: t('homeWorkflow.step5Title'),
            cardDescription: t('homeWorkflow.step5Description'),
            previewBadge: t('homeWorkflow.preview5Badge'),
            previewTitle: t('homeWorkflow.preview5Title'),
            previewDescription: t('homeWorkflow.preview5Description'),
            checklist: [
                t('homeWorkflow.preview5Check1'),
                t('homeWorkflow.preview5Check2'),
                t('homeWorkflow.preview5Check3'),
            ],
            screenTag: t('homeWorkflow.preview5ScreenTag'),
        },
    ];

    const active = steps[activeStep];

    return (
        <section className="workflow-section sp-90">
            <div className="container">
                <MainHeading headingName={t('homeWorkflow.heading')} />
                <p className="paragraph p-adjustment text-center workflow-subheading m-center">
                    {t('homeWorkflow.subheading')}
                </p>

                <div className="workflow-pills spt-60" role="tablist">
                    {steps.map((step, index) => (
                        <button
                            key={step.key}
                            type="button"
                            role="tab"
                            aria-selected={index === activeStep}
                            className={`workflow-pill${index === activeStep ? ' active' : ''}`}
                            onClick={() => setActiveStep(index)}
                        >
                            <span className="workflow-pill-number">{index + 1}</span>
                            <span className="workflow-pill-label">{step.pill}</span>
                        </button>
                    ))}
                </div>

                <div className="workflow-steps-grid">
                    {steps.map((step, index) => (
                        <div
                            key={step.key}
                            className={`workflow-step-card${index === activeStep ? ' active' : ''}`}
                            onClick={() => setActiveStep(index)}
                        >
                            <div className="workflow-step-card-top">
                                <div className="workflow-step-icon">{step.icon}</div>
                                <div className="workflow-step-number">{String(index + 1).padStart(2, '0')}</div>
                            </div>
                            <h3 className="h-5 bold-500 workflow-step-title">{step.cardTitle}</h3>
                            <p className="paragraph p-adjustment mb-0">{step.cardDescription}</p>
                        </div>
                    ))}
                </div>

                <div className="workflow-preview">
                    <span className="workflow-preview-badge">{active.previewBadge}</span>

                    <div className="workflow-preview-inner">
                        <div className="workflow-preview-copy">
                            <div className="workflow-preview-icon">{active.icon}</div>
                            <h3 className="h-4 bold-600">{active.previewTitle}</h3>
                            <p className="paragraph p-adjustment">{active.previewDescription}</p>
                            <ul className="workflow-preview-checklist">
                                {active.checklist.map((item) => (
                                    <li key={item}>
                                        <span className="workflow-check-tick">✓</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="workflow-preview-screen">
                            <div className="workflow-screen-topbar">
                                <span className="workflow-screen-dot" />
                                <span className="workflow-screen-dot" />
                                <span className="workflow-screen-dot" />
                                <span className="workflow-screen-tag">{active.screenTag}</span>
                            </div>
                            <div className="workflow-screen-content">
                                {active.checklist.map((item) => (
                                    <div className="workflow-screen-row" key={item}>
                                        <span className="workflow-check-tick">✓</span>
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="workflow-cta-wrap">
                    <button
                        className="invoice-generator-btn"
                        onClick={() => document.getElementById('get-started').scrollIntoView({ behavior: 'smooth' })}
                    >
                        {t('homeWorkflow.cta')}
                    </button>
                </div>
            </div>
        </section>
    );
}
